/**
 * Copyright 2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useMemo, useState } from "react";
import { RowSelectionState } from "@tanstack/table-core";
import { GetEventsViewQuery, EventStatus } from "~/generated/graphql";
import { useToggle } from "~/hooks";
import { isNotEmpty, sortItem } from "~/tools";
import { EventEntity } from "../../types";
import {
  buildErrorCodeCountsByActionId,
  getActionDescriptionForEntity,
  getActionErrorCodeCounts,
  getFlatActionEntities,
  getExpandedEventEntities,
} from "../EventItem.utils";
import { useEventApproval } from "./useEventApproval";
import { useEventItemDataSync } from "./useEventItemDataSync";
import { useEventItemResourceStates } from "./useEventItemResourceStates";

type Props = {
  event: NonNullable<GetEventsViewQuery["eventsView"]["result"]>[number];
};

export function useEventItem({ event }: Props) {
  const { getEventData, eventSource, liveEventStatus, isEventFinished } =
    useEventItemDataSync({ event });

  const [isExpanded, toggleIsExpanded] = useToggle();
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const listActionEntities = useMemo(
    () => getFlatActionEntities(event),
    [event]
  );
  const flatActionEntities = useMemo(
    () => getFlatActionEntities(eventSource),
    [eventSource]
  );
  const eventStatusIconActions = useMemo(
    () =>
      eventSource?.status === EventStatus.Success
        ? listActionEntities
        : flatActionEntities,
    [eventSource?.status, flatActionEntities, listActionEntities]
  );
  const errorCodeCountsByActionId = useMemo(
    () => buildErrorCodeCountsByActionId(event),
    [event]
  );
  const allActionIds = useMemo(
    () => flatActionEntities.map((action) => action.id),
    [flatActionEntities]
  );
  const { getCountsForAction, updateResourceState } =
    useEventItemResourceStates({
      eventId: event.id,
      allActionIds,
      isExpanded,
      isEventFinished,
    });

  const eventEntities = useMemo(
    () => getExpandedEventEntities(eventSource),
    [eventSource]
  );
  const sortedEventEntities = useMemo(
    () => sortItem(eventEntities, (a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [eventEntities]
  );

  const {
    approveActions,
    approveBatches,
    pendingApprovalLength,
    hasPendingApproval,
    entitiesApprovalBadgeMeta,
    eventApprovalBadgeMeta,
  } = useEventApproval({
    event,
    sortedEventEntities,
  });

  const hasEventEntities = isNotEmpty(listActionEntities);
  const isEventInProgress = liveEventStatus === EventStatus.InProgress;
  const getActionErrorCodeCountsForEntity = (
    eventEntity: Pick<EventEntity, "id" | "attempts">
  ) => getActionErrorCodeCounts(errorCodeCountsByActionId, eventEntity);
  const getActionDescription = (eventEntity: EventEntity) =>
    getActionDescriptionForEntity({
      errorCodeCountsByActionId,
      eventEntity,
      isEventFinished,
    });

  return {
    ui: {
      isExpanded,
      toggleIsExpanded,
    },
    event: {
      eventSource,
      liveEventStatus,
      isEventFinished,
      isEventInProgress,
      updateResourceState,
      refreshEvent: getEventData,
    },
    entities: {
      hasEventEntities,
      eventStatusIconActions,
    },
    table: {
      data: sortedEventEntities,
      rowSelection,
      setRowSelection,
      columnsProps: {
        isEventFinished,
        approvalMetaById: entitiesApprovalBadgeMeta,
        getCountsForAction,
        getActionErrorCodeCounts: getActionErrorCodeCountsForEntity,
        getActionDescription,
      },
    },
    approval: {
      pendingApprovalLength,
      approveActions,
      approveBatches,
      hasPendingApproval,
      eventApprovalBadgeMeta,
    },
  } as const;
}
